import type {
  DoctorEvidenceInboxItem,
  ClinicalEvidenceItem,
} from '@/dashboard/api';
import { DOCTOR_CASE_SHEET_OWED_LABEL } from '@/dashboard/ui/doctor-visit-state';
import {
  consultDoorVerdict,
  type ConsultDoorVerdict,
} from '@/dashboard/doctor/consult-eligibility';

/**
 * Pure view vocabulary + derivations for the Tier-2 clinical-evidence
 * surfaces (DOCTOR-CLINICAL-EVIDENCE-TIER2-V1). First-day-employee words
 * (S-5): controlled tokens render as labeled fields and chips, never raw
 * ids or comma-spliced prose.
 */

/** The Tier-2 class vocabulary labels (DB CHECK superset mirror). */
export const EVIDENCE_CLASS_LABELS: Readonly<Record<string, string>> = {
  case_sheet_page: 'Case-sheet page',
  intraoral_photo: 'Intraoral photo',
  xray: 'X-ray',
  consent_scan: 'Consent scan',
};

export function evidenceClassLabel(token: string): string {
  return EVIDENCE_CLASS_LABELS[token] ?? 'Evidence';
}

/** Void reason labels (the closed clinical_evidence_void_reason_chk list). */
export const EVIDENCE_VOID_REASON_LABELS: ReadonlyArray<{
  readonly token: string;
  readonly label: string;
}> = [
  { token: 'wrong_patient', label: 'Wrong patient' },
  { token: 'wrong_visit', label: 'Wrong visit' },
  { token: 'poor_quality', label: 'Poor quality' },
  { token: 'duplicate', label: 'Duplicate' },
  { token: 'uploaded_in_error', label: 'Uploaded in error' },
];

export function evidenceVoidReasonLabel(token: string): string {
  return (
    EVIDENCE_VOID_REASON_LABELS.find((r) => r.token === token)?.label ??
    'Voided'
  );
}

/** The active (non-voided) items of one class, capture order. */
export function activeItemsOfClass(
  items: readonly ClinicalEvidenceItem[],
  evidenceClass: string,
): readonly ClinicalEvidenceItem[] {
  return items.filter(
    (i) => i.status === 'active' && i.evidenceClass === evidenceClass,
  );
}

/** Voided items (demoted -- visible with their reason, never hidden). */
export function voidedItems(
  items: readonly ClinicalEvidenceItem[],
): readonly ClinicalEvidenceItem[] {
  return items.filter((i) => i.status === 'voided');
}

/** One inbox group: a patient (or the unlinked bucket) + their owed items. */
export interface InboxPatientGroup {
  readonly key: string;
  readonly patientName: string | null;
  readonly patientUmr: string | null;
  readonly items: readonly DoctorEvidenceInboxItem[];
}

/** Group the derived inbox by patient, preserving the server's
 *  oldest-debt-first order within and across groups. F6d (corrective,
 *  founder tower walk 2026-08-14): the display name reads the NAME LADDER
 *  -- the server-resolved GOLDEN name (patients.full_name first) else the
 *  appointment's admin-plane name -- so "Patient record pending" is the
 *  true last resort, never the default for a nameable person. */
export function groupInboxByPatient(
  items: readonly DoctorEvidenceInboxItem[],
): readonly InboxPatientGroup[] {
  const groups = new Map<string, InboxPatientGroup>();
  for (const item of items) {
    const key = item.patient?.id ?? `sheet-${item.caseSheetId}`;
    const existing = groups.get(key);
    if (existing) {
      groups.set(key, { ...existing, items: [...existing.items, item] });
    } else {
      groups.set(key, {
        key,
        patientName: item.patient?.name ?? item.appointmentPatientName ?? null,
        patientUmr: item.patient?.umr ?? null,
        items: [item],
      });
    }
  }
  return [...groups.values()];
}

// --- The HOME dashboard card (DOCTOR-PRECALL-POLISH-V1, DR-20) --------------
//
// The inbox used to render one PARAGRAPH ROW per owed (visit, kind) pair
// under a repeated per-patient header -- a document, not a dashboard card.
// It now composes as ONE ROW PER PATIENT: name chip + owed badges + ONE
// Capture door, newest visit first, capped with a "view all" expander.
//
// Every derivation below is PURE and display-only. The server's derived
// item set is unchanged (no new wire, no new read); nothing is dropped --
// every owed item is counted into a badge, and the capped rows stay
// reachable behind the expander (EVIDENCE-ROWS LAW: nothing is deleted).

/** How many patient rows the card shows before the expander (DR-20 "~5"). */
export const INBOX_CARD_ROW_CAP = 5;

/** One owed-class badge on a patient row: "Case-sheet page x2". */
export interface InboxOwedBadge {
  readonly kind: string;
  /** The class label + the count, always explicit (never a bare label that
   *  hides a multiple). */
  readonly label: string;
  readonly count: number;
}

/** ONE dashboard-card row = ONE patient. */
export interface InboxPatientCardRow {
  readonly key: string;
  readonly patientName: string | null;
  readonly patientUmr: string | null;
  /** Owed classes for this patient, first-seen order (the server's
   *  oldest-debt-first order), with counts. */
  readonly owed: readonly InboxOwedBadge[];
  /** What the row's ONE Capture door opens: the patient's OLDEST owed item
   *  (the server orders oldest debt first, so it is the first item). Clearing
   *  it re-derives the row on refetch and the next-oldest takes the door --
   *  the row drains, it never dead-ends. */
  readonly captureItem: DoctorEvidenceInboxItem;
  /** The NEWEST visit date across this patient's owed items -- the card's
   *  sort key (DR-20: newest first). ISO string, straight from the wire. */
  readonly newestVisitDate: string;
  /** Total owed items behind this row (the sum of the badge counts). */
  readonly itemCount: number;
}

/** Badge text: the class label and its count, e.g. "Case-sheet page x2". */
export function inboxOwedBadgeLabel(kind: string, count: number): string {
  return `${evidenceClassLabel(kind)} x${count}`;
}

/**
 * The card rows: group by patient, count owed classes, sort NEWEST VISIT
 * FIRST. Total and deterministic -- ties break on the group key, so the
 * order never depends on the incoming sequence.
 */
export function inboxPatientCardRows(
  items: readonly DoctorEvidenceInboxItem[],
): readonly InboxPatientCardRow[] {
  const rows = groupInboxByPatient(items).map((group) => {
    const counts = new Map<string, number>();
    for (const item of group.items) {
      counts.set(item.kind, (counts.get(item.kind) ?? 0) + 1);
    }
    const owed = [...counts.entries()].map(([kind, count]) => ({
      kind,
      label: inboxOwedBadgeLabel(kind, count),
      count,
    }));
    // NEWEST across the patient's owed items. An unparseable date never
    // displaces a real one (it sorts last via the -Infinity key below).
    const newestVisitDate = group.items.reduce(
      (newest, item) =>
        visitDateKey(item.visitDate) > visitDateKey(newest) ? item.visitDate : newest,
      group.items[0]!.visitDate,
    );
    return {
      key: group.key,
      patientName: group.patientName,
      patientUmr: group.patientUmr,
      owed,
      // The server orders oldest debt first, so item 0 IS the oldest.
      captureItem: group.items[0]!,
      newestVisitDate,
      itemCount: group.items.length,
    };
  });
  return rows.sort((a, b) => {
    const byDate = visitDateKey(b.newestVisitDate) - visitDateKey(a.newestVisitDate);
    if (byDate !== 0) return byDate;
    return a.key.localeCompare(b.key);
  });
}

/** An ISO visit date as a comparable epoch; unparseable sorts LAST under the
 *  descending comparator above (never displaces a row with a real date). */
function visitDateKey(value: string): number {
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? -Infinity : ms;
}

/** An ISO visit date as an ASCENDING epoch key for OLDEST-DEBT-FIRST ordering
 *  (CORRECTIVE ROUND 1): the earliest real date leads, and an unparseable date
 *  sorts LAST (+Infinity) -- it never masquerades as the oldest debt. */
function oldestDebtKey(value: string): number {
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? Infinity : ms;
}

// --- OWED WORK: draft case sheets (DOCTOR-OWED-WORK-QUEUE-V1, category A) ----
//
// The inbox rail is EXTENDED into "Owed work" (founder ruling Q2, 2026-08-10:
// no new route, no new visual language). Category A is the doctor's own
// UNFINISHED (draft) case sheets: a draft row wears the D52 "Case sheet
// draft" chip + a since-visit age and opens ONE door -- Resume consult --
// via consultPathForAppointment. Owed-evidence rows (category B) keep their
// in-place Capture door unchanged. Every derivation here is PURE and
// display-only; nothing is stored.

/** The reserved wire `kind` for a category-A draft item. MUST equal the
 *  server's DRAFT_CASE_SHEET_KIND (lib/domain/clinical/evidence-pending.ts);
 *  a cross-boundary literal pinned in both suites keeps the two identical. */
export const DRAFT_CASE_SHEET_KIND = 'draft_case_sheet';

/** The draft chip copy -- inherited verbatim from the day worklist's D52
 *  draft signal (never a new label). */
/** DOCTOR-HOME-STATE-MODEL-V1 (F6c): the owed-paperwork statement is the
 *  state owner's ONE vocabulary -- the day row's draft chip and this inbox
 *  chip say the same words ("Case sheet owed": WHAT is owed, not just that
 *  a draft exists). */
export const DRAFT_CASE_SHEET_CHIP_LABEL = DOCTOR_CASE_SHEET_OWED_LABEL;

/** The since-visit age for a draft row: "Today" / "Yesterday" / "N days ago".
 *  Pure -- the caller passes `nowMs` (no clock inside a view derivation). An
 *  unparseable date yields null (the row renders with no fabricated age). */
export function sinceVisitLabel(
  visitDate: string,
  nowMs: number,
): string | null {
  const ms = Date.parse(visitDate);
  if (Number.isNaN(ms)) return null;
  const days = Math.floor((nowMs - ms) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

/** ONE owed-work row for a patient's draft case sheet(s). */
export interface DraftCaseSheetRow {
  readonly key: string;
  readonly patientName: string | null;
  readonly patientUmr: string | null;
  /** What the ONE Resume door opens: the patient's oldest draft whose
   *  appointment a consult may lawfully be RESUMED on (THE SHARED
   *  eligibility rule). The server orders oldest-debt-first, so that is the
   *  first eligible item; when the patient has NO resumable draft it is the
   *  oldest draft, and `door` below is `blocked` -- the row still names the
   *  debt, it just stops offering a press the server will refuse.
   *
   *  The row's oldest-debt SORT KEY is `sinceVisitDate`, never this item's
   *  date: the queue leads with the longest-owed work whether or not that
   *  particular visit can be resumed (CORRECTIVE ROUND 1 order, unchanged). */
  readonly resumeItem: DoctorEvidenceInboxItem;
  /** Whether this row offers the Resume door -- THE SHARED eligibility rule's
   *  verdict over `resumeItem`'s appointment status (Lane 1 item 1 / D01).
   *  `blocked` carries the sentence the row shows INSTEAD of the door; on a
   *  pre-corrective server the verdict is `unknown` and the door stands
   *  exactly as it does today. */
  readonly door: ConsultDoorVerdict;
  /** The oldest draft's visit date -- the since-age the chip renders AND the
   *  oldest-debt sort key (the longest a sheet has sat unfinished). */
  readonly sinceVisitDate: string;
  /** The newest draft visit -- a descriptive fact only (the row sorts
   *  OLDEST-DEBT-first, not by this; CORRECTIVE ROUND 1). */
  readonly newestVisitDate: string;
  /** How many draft sheets sit behind this one row. */
  readonly draftCount: number;
}

/**
 * The draft rows: keep only category-A items, group by patient (one row),
 * OLDEST DEBT FIRST -- the patient whose oldest draft has sat unfinished the
 * longest leads (CORRECTIVE ROUND 1, founder-ruled; the package contract's
 * "oldest-first"). Total and deterministic -- ties break on the group key,
 * so order never depends on the incoming sequence.
 */
export function draftCaseSheetRows(
  items: readonly DoctorEvidenceInboxItem[],
): readonly DraftCaseSheetRow[] {
  const drafts = items.filter((i) => i.kind === DRAFT_CASE_SHEET_KIND);
  const rows = groupInboxByPatient(drafts).map((group) => {
    const newestVisitDate = group.items.reduce(
      (newest, item) =>
        visitDateKey(item.visitDate) > visitDateKey(newest)
          ? item.visitDate
          : newest,
      group.items[0]!.visitDate,
    );
    // THE DOOR TARGET IS THE OLDEST *RESUMABLE* DRAFT (Lane 1 item 1 / D01).
    // A patient row can group several drafts, and one of them being cancelled
    // is no reason to withhold the doctor's work on the others -- so the
    // press opens the oldest draft the shared rule admits. Only when NO draft
    // in the group is resumable does the row fall back to the oldest one and
    // render its state instead of a door.
    //
    // The server already orders oldest-debt-first, so the FIRST match is the
    // oldest match; `find` preserves that order without re-sorting.
    const oldest = group.items[0]!;
    const resumable = group.items.find(
      (item) => consultDoorVerdict(item.appointmentStatus).kind !== 'blocked',
    );
    const resumeItem = resumable ?? oldest;
    return {
      key: group.key,
      patientName: group.patientName,
      patientUmr: group.patientUmr,
      resumeItem,
      door: consultDoorVerdict(resumeItem.appointmentStatus),
      // The DEBT is the oldest draft's age, whatever the door opens.
      sinceVisitDate: oldest.visitDate,
      newestVisitDate,
      draftCount: group.items.length,
    };
  });
  return rows.sort((a, b) => {
    // OLDEST DEBT FIRST: the patient's oldest draft (sinceVisitDate) ascending.
    const byDebtAge =
      oldestDebtKey(a.sinceVisitDate) - oldestDebtKey(b.sinceVisitDate);
    if (byDebtAge !== 0) return byDebtAge;
    return a.key.localeCompare(b.key);
  });
}

/** ONE unified owed-work row: a draft-case-sheet row (Resume) or an owed-
 *  evidence row (Capture). `oldestDebtDate` is the visit date of the item the
 *  row's ONE door opens (the oldest draft / the oldest owed evidence) -- the
 *  row's oldest-debt sort key. The rail renders one capped, OLDEST-DEBT-FIRST
 *  list (CORRECTIVE ROUND 1). */
export type OwedWorkRow =
  | {
      readonly rowType: 'draft';
      readonly key: string;
      readonly oldestDebtDate: string;
      readonly draft: DraftCaseSheetRow;
    }
  | {
      readonly rowType: 'evidence';
      readonly key: string;
      readonly oldestDebtDate: string;
      readonly evidence: InboxPatientCardRow;
    };

/**
 * The owed-work queue rows: category A (draft rows) + category B (owed-
 * evidence rows), one OLDEST-DEBT-FIRST list -- draft and evidence rows
 * interleaved by the age of the debt, the longest-owed at the top
 * (CORRECTIVE ROUND 1, founder-ruled: the package contract's "oldest-first").
 * The oldest-debt key per row is the visit date of the item its ONE door
 * opens (draft: the oldest draft; evidence: the oldest owed item -- both the
 * server's item-0). Each row's DOOR already opens that oldest item, so the
 * list order and the door target agree.
 *
 * Category B rows are built from FINALIZED sheets only: a draft sheet's own
 * owed evidence is represented by its draft row (Resume subsumes it --
 * capture happens inside the workspace), never a duplicate Capture row for a
 * sheet the doctor has not finished. The evidence-row grouping
 * (inboxPatientCardRows -- DR-20's evidence card, byte-unchanged; its own
 * newest-first order is a standalone contract that this merged list
 * re-sorts) is reused only for grouping + the oldest-owed door + badges.
 * Nothing is dropped: a finalized sheet still owing evidence is a category-B
 * row; a draft sheet is a category-A row.
 */
export function owedWorkRows(
  items: readonly DoctorEvidenceInboxItem[],
): readonly OwedWorkRow[] {
  const draftRows = draftCaseSheetRows(items);
  const evidenceItems = items.filter(
    (i) => i.kind !== DRAFT_CASE_SHEET_KIND && i.sheetStatus === 'finalized',
  );
  const evidenceRows = inboxPatientCardRows(evidenceItems);
  const rows: OwedWorkRow[] = [
    ...draftRows.map(
      (d): OwedWorkRow => ({
        rowType: 'draft',
        key: `draft-${d.key}`,
        // The OLDEST DRAFT is the row's debt age -- `sinceVisitDate`, not the
        // door's target, which may now be a newer resumable draft when the
        // oldest one is cancelled (Lane 1 item 1 / D01). Identical value for
        // every row whose oldest draft is resumable, so the ruled
        // oldest-debt-first order is unchanged.
        oldestDebtDate: d.sinceVisitDate,
        draft: d,
      }),
    ),
    ...evidenceRows.map(
      (e): OwedWorkRow => ({
        rowType: 'evidence',
        key: `evidence-${e.key}`,
        // The oldest owed item (the Capture door's target) IS the debt age.
        oldestDebtDate: e.captureItem.visitDate,
        evidence: e,
      }),
    ),
  ];
  return rows.sort((a, b) => {
    // OLDEST DEBT FIRST: earliest debt-start date at the top; an unparseable
    // date never displaces a real debt (it sorts LAST via oldestDebtKey).
    const byDebtAge =
      oldestDebtKey(a.oldestDebtDate) - oldestDebtKey(b.oldestDebtDate);
    if (byDebtAge !== 0) return byDebtAge;
    return a.key.localeCompare(b.key);
  });
}

/**
 * The close-summary / panel owed line. Empty list = nothing owed.
 *
 * THE WORD "OWED" NAMES ITS SUBJECT HERE (founder finding, #780 review). This
 * string renders in TWO places with two different subjects around it: in the
 * Case-sheet card it means EVIDENCE, and in the Close card it lands directly
 * under "Front desk collects - Rs N,NNN suggested from the accepted plan",
 * where a dentist reads "Nothing owed for this visit" as MONEY. Same sentence,
 * opposite meaning, one line apart.
 *
 * One word never carries two meanings, so the evidence line says evidence. The
 * money line is not touched -- it is a different owner's sentence and it was
 * never the ambiguous half.
 */
export function owedKindsSummary(owedKinds: readonly string[]): string {
  if (owedKinds.length === 0) return 'No evidence owed for this visit.';
  return `Evidence still owed: ${owedKinds.map(evidenceClassLabel).join(', ')}`;
}

/* ---------------------------------------------------------------------------
 * DOCTOR-TODAY-CRAFT-V1 -- THE OWED-WORK BECAUSE-LINE + THE KIND SPLIT.
 *
 * Doctor Today answers two questions: what do I do next, and what do I still
 * owe. The second answer was a bare list under the words "Owed work" -- a
 * count the doctor had to derive by scrolling, with no statement of how old
 * the debt was or what kind of debt it is.
 *
 * EVERY FIGURE BELOW COMES OFF ROWS ALREADY LOADED. `OwedWorkRow` already
 * carries `oldestDebtDate` on BOTH variants and `rowType` already names the
 * kind, so the split, the age and the order clause are derivations over the
 * SAME read -- no new query, no read-model change, no historical data. Pure
 * and total: an unparseable date yields absence, never a guessed age.
 * ------------------------------------------------------------------------ */

/** The on-glass order statement for the owed-work queue. The queue has always
 *  sorted oldest-debt-first (`owedWorkRows`); this is that existing rule said
 *  out loud, in the register-order idiom the follow-ups queue established. */
export const OWED_WORK_ORDER_CLAUSE = 'oldest first';

export interface OwedWorkSummary {
  readonly total: number;
  readonly draftCount: number;
  readonly evidenceCount: number;
  /** Age of the OLDEST debt in whole days; null when no row carries a
   *  parseable visit date (honest absence, never a zero standing in for
   *  "unknown"). */
  readonly oldestDays: number | null;
}

/** Whole days between a visit date and now; null when unparseable. Shares the
 *  floor-to-days arithmetic `sinceVisitLabel` uses, so the section line and
 *  the row lines can never disagree about an age. */
export function owedDebtDays(visitDate: string, nowMs: number): number | null {
  const ms = Date.parse(visitDate);
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.floor((nowMs - ms) / 86_400_000));
}

/** THE ROW AGE, for BOTH row kinds. The draft row already showed a since-age;
 *  the evidence row showed none, so identical debts read differently depending
 *  on which kind they were. One expression now serves both off the SAME
 *  `oldestDebtDate` the queue already sorts on. */
export function owedRowAgeLabel(row: OwedWorkRow, nowMs: number): string | null {
  return sinceVisitLabel(row.oldestDebtDate, nowMs);
}

/** The counts + the oldest debt, derived from the loaded rows. */
export function owedWorkSummary(
  rows: readonly OwedWorkRow[],
  nowMs: number,
): OwedWorkSummary {
  let draftCount = 0;
  let evidenceCount = 0;
  let oldestDays: number | null = null;
  for (const row of rows) {
    if (row.rowType === 'draft') draftCount += 1;
    else evidenceCount += 1;
    const days = owedDebtDays(row.oldestDebtDate, nowMs);
    if (days !== null && (oldestDays === null || days > oldestDays)) {
      oldestDays = days;
    }
  }
  return { total: rows.length, draftCount, evidenceCount, oldestDays };
}

/** The kind's plain-words label, singular-aware. */
export function owedKindLabel(kind: 'draft' | 'evidence', count: number): string {
  if (kind === 'draft') {
    return `${count} case ${count === 1 ? 'sheet' : 'sheets'} to finish`;
  }
  return `${count} ${count === 1 ? 'visit' : 'visits'} missing evidence`;
}

/**
 * THE BECAUSE-LINE. What is owed, split by kind, how old the oldest debt is,
 * and the order the list is in -- one sentence, every clause backed by a
 * figure the rows below actually render.
 *
 *   "11 case sheets to finish, 18 visits missing evidence - oldest 29 days -
 *    oldest first"
 *
 * A kind with no rows is OMITTED rather than printed as a zero (the same
 * rule the day ribbon applies to a zero cell: a zero is not a fact worth a
 * slot). An unparseable oldest date drops the age clause instead of guessing.
 * Empty input returns null -- the caller renders its own honest empty line,
 * because "nothing owed" is a different sentence, not an emptier version of
 * this one.
 */
export function owedWorkBecauseLine(summary: OwedWorkSummary): string | null {
  if (summary.total === 0) return null;
  const kinds: string[] = [];
  if (summary.draftCount > 0) kinds.push(owedKindLabel('draft', summary.draftCount));
  if (summary.evidenceCount > 0) {
    kinds.push(owedKindLabel('evidence', summary.evidenceCount));
  }
  const clauses = [kinds.join(', ')];
  if (summary.oldestDays !== null) {
    clauses.push(
      summary.oldestDays <= 0
        ? 'oldest from today'
        : `oldest ${summary.oldestDays} ${summary.oldestDays === 1 ? 'day' : 'days'}`,
    );
  }
  clauses.push(OWED_WORK_ORDER_CLAUSE);
  return clauses.join(' - ');
}

/* THE KIND FILTER WAS BUILT AND THEN REMOVED, deliberately. Chips reading
 * "All 29 / To finish 15 / Missing evidence 14" would have let the doctor
 * isolate one kind of debt -- but the section's because-line already states
 * that split in words, so the chips bought a shortcut at the cost of three
 * more elements on a screen whose ruling (founder, 2026-09-01) is that the
 * element count must go DOWN. The split survives as a sentence; the chips do
 * not survive at all. Recorded here rather than left as unused exports,
 * because dead code that reads as a feature is worse than no code. */
